import { getBaseButtonStyles, loadBaseCSS } from './button-utils.js';
import { attachInternalsSafe, isAttrTruthy, parseSize, reflectBoolean, upgradeProperty } from './form-control.js';

export type QdsButtonVariant = 'primary' | 'secondary' | 'quiet' | 'destructive';

/** Shared native activation and form submission for text and icon buttons. */
export class QdsButtonBase extends HTMLElement {
  static formAssociated = true;
  static get observedAttributes(): string[] {
    return ['label', 'aria-label', 'aria-pressed', 'aria-expanded', 'aria-controls', 'title',
      'variant', 'size', 'disabled', 'loading', 'type', 'name', 'value', 'form',
      'formaction', 'formenctype', 'formmethod', 'formnovalidate', 'formtarget'];
  }
  protected _button?: HTMLButtonElement;
  protected _internals?: ElementInternals;
  private _fieldsetDisabled = false;
  private _onClick = (event: Event): void => {
    event.stopPropagation();
    if (this.disabled || this.loading) { event.preventDefault(); return; }
    const activation = new CustomEvent('click', {
      bubbles: true, composed: true, cancelable: true, detail: { component: this },
    });
    if (!this.dispatchEvent(activation) || event.defaultPrevented || !this._activate()) return;
    const form = this.form;
    if (!form || this.type === 'button') return;
    const prototype = form.ownerDocument.defaultView!.HTMLFormElement.prototype;
    if (this.type === 'reset') { prototype.reset.call(form); return; }
    // requestSubmit requires a native submitter. A temporary button preserves
    // validation, submitter name/value, and per-button form overrides.
    const submitter = document.createElement('button');
    submitter.type = 'submit';
    submitter.hidden = true;
    submitter.name = this.name;
    submitter.value = this.value;
    for (const name of ['formaction', 'formenctype', 'formmethod', 'formnovalidate', 'formtarget']) {
      if (this.hasAttribute(name)) submitter.setAttribute(name, this.getAttribute(name)!);
    }
    form.appendChild(submitter);
    try { prototype.requestSubmit.call(form, submitter); } finally { submitter.remove(); }
  };
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    loadBaseCSS();
    const internals = attachInternalsSafe(this);
    if (typeof internals?.setFormValue === 'function') this._internals = internals;
  }
  protected get _iconOnly(): boolean { return false; }
  protected get _defaultVariant(): QdsButtonVariant { return 'primary'; }
  protected get _fallbackName(): string { return ''; }
  /** Specializations may handle activation without submitting/resetting a form. */
  protected _activate(): boolean { return true; }
  protected _updateIcon(): void { /* Slotted icons need no runtime download. */ }
  get label(): string { return this.getAttribute('label') || ''; }
  set label(value: string) { this.setAttribute('label', String(value ?? '')); }
  get disabled(): boolean { return this._fieldsetDisabled || isAttrTruthy(this.getAttribute('disabled')); }
  set disabled(value: boolean) { reflectBoolean(this, 'disabled', value); }
  get loading(): boolean { return isAttrTruthy(this.getAttribute('loading')); }
  set loading(value: boolean) { reflectBoolean(this, 'loading', value); }
  get type(): 'button' | 'submit' | 'reset' {
    const value = this.getAttribute('type');
    return value === 'submit' || value === 'reset' ? value : 'button';
  }
  set type(value: string) { this.setAttribute('type', value); }
  get variant(): QdsButtonVariant {
    const value = this.getAttribute('variant');
    return value === 'primary' || value === 'secondary' || value === 'quiet' || value === 'destructive' ? value : this._defaultVariant;
  }
  set variant(value: string) { this.setAttribute('variant', value); }
  get size(): 'sm' | 'md' | 'lg' { return parseSize(this.getAttribute('size')); }
  set size(value: string) { this.setAttribute('size', value); }
  get name(): string { return this.getAttribute('name') || ''; }
  set name(value: string) { this.setAttribute('name', value); }
  get value(): string { return this.getAttribute('value') || ''; }
  set value(value: string) { this.setAttribute('value', value); }
  get form(): HTMLFormElement | null {
    if (this._internals) return this._internals.form;
    const id = this.getAttribute('form');
    const target = id ? this.ownerDocument.getElementById(id) : this.closest('form');
    return target?.tagName === 'FORM' ? target as HTMLFormElement : null;
  }
  connectedCallback(): void {
    for (const name of ['label', 'disabled', 'loading', 'type', 'variant', 'size', 'name', 'value']) upgradeProperty(this, name);
    if (!this._button) this._render();
    this._button!.addEventListener('click', this._onClick);
    this._sync();
  }
  disconnectedCallback(): void { this._button?.removeEventListener('click', this._onClick); }
  attributeChangedCallback(): void { if (this._button) this._sync(); }
  formDisabledCallback(disabled: boolean): void { this._fieldsetDisabled = disabled; this._sync(); }
  override focus(options?: FocusOptions): void { this._button?.focus(options); }
  override click(): void { this._button?.click(); }
  private _render(): void {
    this.shadowRoot!.innerHTML = `<style>${getBaseButtonStyles()}
      :host { vertical-align: middle; font-family: var(--theme-font-family, 'IBM Plex Sans', sans-serif); }
      button { width: auto; min-width: 44px; min-height: 44px; height: auto; padding: 10px 16px;
        gap: 8px; font: inherit; font-size: 14px; line-height: 1.4; font-weight: 500;
        border: 1px solid transparent; background: var(--theme-accent-primary, #445e89);
        color: var(--theme-text-on-accent-primary, #fff); }
      button[data-size="sm"] { padding: 8px 12px; font-size: 13px; }
      button[data-size="lg"] { min-height: 48px; padding: 12px 20px; font-size: 16px; }
      button[data-variant="secondary"] { background: var(--theme-background-primary, #fff); color: var(--theme-text-primary, #333); border-color: var(--theme-border-secondary, #a0aabe); }
      button[data-variant="quiet"] { background: transparent; color: var(--theme-text-primary, #333); }
      button[data-variant="destructive"] { background: var(--theme-error, #8b1f00); color: var(--theme-text-on-error, #fff); }
      button:hover:not(:disabled) { box-shadow: inset 0 0 0 100px var(--button-hover-bg, rgba(0,0,0,.05)); }
      button:focus, button:focus-visible { outline: 2px solid var(--theme-accent-primary, #445e89); outline-offset: 2px; box-shadow: none; }
      button:disabled { opacity: .55; cursor: not-allowed; }
      button[data-icon-only] { width: var(--button-control-size, 44px); padding: 0; }
      button[data-icon-only][data-size="lg"] { width: 48px; }
      [hidden] { display: none !important; }
      .spinner { width: 1em; height: 1em; border: 2px solid currentColor; border-inline-end-color: transparent; border-radius: 50%; animation: qds-button-spin .8s linear infinite; }
      @keyframes qds-button-spin { to { transform: rotate(360deg); } }
      @media (prefers-reduced-motion: reduce) { button { transition: none; } .spinner { animation: none; } }
      @media (forced-colors: active) { button { border: 1px solid ButtonText; } button:disabled { color: GrayText; } }
    </style><button type="button" part="button"><span class="spinner" part="spinner" aria-hidden="true" hidden></span><span class="leading" part="icon"><slot name="icon"></slot></span><slot class="label" part="label"></slot><slot name="suffix" part="suffix"></slot></button>`;
    this._button = this.shadowRoot!.querySelector('button')!;
  }
  private _sync(): void {
    if (!this._button) return;
    this._button.disabled = this.disabled || this.loading;
    this._button.dataset.variant = this.variant;
    this._button.dataset.size = this.size;
    this._button.toggleAttribute('data-icon-only', this._iconOnly);
    this._button.setAttribute('aria-busy', String(this.loading));
    // Text buttons inherit their name from rendered slot content (including the
    // label fallback). Icon-only buttons still need an explicit text alternative.
    const name = this.getAttribute('aria-label') || (this._iconOnly ? this.label || this._fallbackName : '');
    if (name) this._button.setAttribute('aria-label', name); else this._button.removeAttribute('aria-label');
    for (const name of ['aria-pressed', 'aria-expanded', 'aria-controls', 'title']) {
      const value = this.getAttribute(name);
      if (value === null) this._button.removeAttribute(name); else this._button.setAttribute(name, value);
    }
    this.shadowRoot!.querySelector('slot.label')!.textContent = this.label;
    (this.shadowRoot!.querySelector('.spinner') as HTMLElement).hidden = !this.loading;
    this._internals?.setFormValue(null);
    this._updateIcon();
  }
}
